import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, User, Video, ExternalLink } from 'lucide-react';
import { calendarService } from '../../services/api';
import useGoogleCalendarSync from '../../hooks/useGoogleCalendarSync';
import useOutlookCalendarSync from '../../hooks/useOutlookCalendarSync';

const TodaysScheduleWidget = ({ userId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchTodaysEvents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await calendarService.getTodaysEvents(userId);
      const allEvents = response.data?.events || [];

      // Keep events that overlap the rest of today (local time): not yet ended,
      // and starting before midnight. Includes events that began yesterday.
      const now = new Date();
      const endOfToday = new Date(now);
      endOfToday.setHours(23, 59, 59, 999);
      const upcomingEvents = allEvents.filter(event => {
        const startTime = new Date(event.start_time);
        const endTime = new Date(event.end_time);
        return startTime <= endOfToday && endTime > now;
      });

      setEvents(upcomingEvents);
    } catch (error) {
      console.error('Error fetching today\'s events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchTodaysEvents();
  }, [fetchTodaysEvents]);

  // Poll Google Calendar for new events every 60s while widget is mounted
  useGoogleCalendarSync({
    onSyncComplete: () => fetchTodaysEvents()
  });

  // Poll Outlook Calendar for new events every 60s while widget is mounted
  useOutlookCalendarSync({
    onSyncComplete: () => fetchTodaysEvents()
  });

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getEventTypeIcon = (event) => {
    if (event.location && event.location.toLowerCase().includes('zoom')) {
      return <Video className="h-4 w-4" />;
    }
    if (event.location) {
      return <MapPin className="h-4 w-4" />;
    }
    return <Calendar className="h-4 w-4" />;
  };

  const handleEventClick = (event) => {
    navigate('/calendar');
  };

  const handleViewCalendar = () => {
    navigate('/calendar');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border overflow-hidden">
      {/* Widget Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Calendar className="h-5 w-5 text-gray-600 mr-2" />
            Today's Schedule
          </h3>
          <button
            onClick={handleViewCalendar}
            className="text-yellow-600 hover:text-gray-800 text-sm font-medium flex items-center"
          >
            View Calendar
            <ExternalLink className="h-4 w-4 ml-1" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      {/* Widget Content */}
      <div className="p-6">
        {events.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 font-medium">No events scheduled today</p>
            <p className="text-gray-500 text-sm">Enjoy your free time!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.map((event) => (
              <div
                key={event.id}
                onClick={() => handleEventClick(event)}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-blue-300 transition-colors cursor-pointer overflow-hidden"
              >
                <div className="flex items-start justify-between min-w-0">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 mt-1">
                      {getEventTypeIcon(event)}
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <h4 className="font-medium text-gray-900 truncate">
                        {event.title}
                      </h4>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                        <span className="flex items-center flex-shrink-0">
                          <Clock className="h-4 w-4 mr-1 flex-shrink-0" />
                          {formatTime(event.start_time)} - {formatTime(event.end_time)}
                        </span>
                        {event.case_title && (
                          <span className="flex items-center text-yellow-600 min-w-0">
                            <User className="h-4 w-4 mr-1 flex-shrink-0" />
                            <span className="truncate">{event.case_title}</span>
                          </span>
                        )}
                      </div>
                      {event.description && (
                        <p className="mt-2 text-sm text-gray-500 truncate">
                          {event.description}
                        </p>
                      )}
                    </div>
                  </div>
                  {event.all_day && (
                    <span className="ml-2 px-2 py-1 bg-gray-100 text-yellow-600 text-xs font-medium rounded">
                      All Day
                    </span>
                  )}
                </div>
              </div>
            ))}

            {events.length > 5 && (
              <div className="text-center pt-2">
                <button
                  onClick={handleViewCalendar}
                  className="text-yellow-600 hover:text-gray-800 text-sm font-medium"
                >
                  View all {events.length} events
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TodaysScheduleWidget;
